import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH } from '@fametrue/shared';
import { useAuth } from '../auth/AuthProvider';
import { AuthError, AuthScreen } from '../components/AuthScreen';
import { useErrorMessage } from '../lib/useErrorMessage';

/**
 * Cambio password volontario, raggiungibile dal menu utente.
 *
 * Schermata non presente nell'Allegato A, costruita sul guscio della sua login
 * come la pagina di invito: è una schermata di credenziali, non una vista della
 * dashboard.
 *
 * Non è un recupero password: serve quella attuale. Chi l'ha dimenticata passa
 * dall'amministratore, che riemette l'invito — la piattaforma non invia e-mail
 * (offerta §2) e non ha un provider SMTP (DPA, Allegato 1: nessun
 * sub-responsabile).
 */
export function ChangePasswordPage(): ReactNode {
  const { t } = useTranslation();
  const { changePassword } = useAuth();
  const navigate = useNavigate();
  const describeError = useErrorMessage();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setLocalError(null);

    if (next !== confirmation) {
      setLocalError(t('password.mismatch'));
      return;
    }
    if (next === current) {
      setLocalError(t('password.sameAsCurrent'));
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(current, next);
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirmation('');
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  const message = localError ?? describeError(error);

  if (done) {
    return (
      <AuthScreen subtitle={t('password.subtitle')}>
        <p className="login-lead">{t('password.done')}</p>
        <button type="button" className="login-btn" onClick={() => void navigate('/')}>
          {t('password.backToDashboard')}
        </button>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen subtitle={t('password.subtitle')}>
      <form onSubmit={(e) => void onSubmit(e)}>
        <div className="login-group">
          <label className="login-label" htmlFor="password-current">
            {t('password.current')}
          </label>
          <input
            id="password-current"
            type="password"
            className="login-field"
            autoComplete="current-password"
            autoFocus
            required
            disabled={submitting}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>

        <div className="login-group">
          <label className="login-label" htmlFor="password-new">
            {t('password.next')}
          </label>
          <input
            id="password-new"
            type="password"
            className="login-field"
            autoComplete="new-password"
            required
            minLength={PASSWORD_MIN_LENGTH}
            disabled={submitting}
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </div>

        <div className="login-group">
          <label className="login-label" htmlFor="password-confirm">
            {t('password.confirm')}
          </label>
          <input
            id="password-confirm"
            type="password"
            className="login-field"
            autoComplete="new-password"
            required
            disabled={submitting}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />
        </div>

        <p className="login-hint">{t('password.hint')}</p>

        {message ? <AuthError message={message} /> : null}

        <button type="submit" className="login-btn" disabled={submitting}>
          {submitting ? t('password.submitting') : t('password.submit')}
        </button>
        <button
          type="button"
          className="login-link"
          disabled={submitting}
          onClick={() => void navigate('/')}
        >
          {t('password.cancel')}
        </button>
      </form>
    </AuthScreen>
  );
}
